import { useState } from 'react';
import { useReportBuilderStore } from '../../../stores/reportBuilderStore';
import FilterBuilder from '../FilterBuilder';
import SortBuilder from '../SortBuilder';
import HavingFilterBuilder from '../HavingFilterBuilder';
import PeriodComparisonPanel from '../PeriodComparisonPanel';
import WindowFunctionBuilder from '../WindowFunctionBuilder';
import { CalendarBlank, Function as FnIcon, Trash } from '@phosphor-icons/react';
import { WINDOW_FUNC_LABELS } from '../../../types/reportBuilder';
import { FEATURE_FLAGS } from '../../../constants/featureFlags';

export default function FiltersAndSortStep() {
  const {
    selectedModel, selectedMeasures,
    windowFunctions, removeWindowFunction,
    periodComparison, getDateColumns,
  } = useReportBuilderStore();

  const [showPeriodPanel, setShowPeriodPanel] = useState(false);
  const [showWindowBuilder, setShowWindowBuilder] = useState(false);

  if (!selectedModel) return null;

  const hasMeasures = selectedMeasures.length > 0;
  const dateColumns = getDateColumns();
  const hasDateColumns = dateColumns.length > 0;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Filters & Sorting</h2>
        <p className="mt-1 text-sm text-gray-500">
          Optionally add filters to narrow down results and sorting to control the order. You can skip this step.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <FilterBuilder />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <SortBuilder />
        </div>
      </div>

      {/* Having Filters — only when measures are selected */}
      {FEATURE_FLAGS.HAVING_FILTERS && hasMeasures && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <HavingFilterBuilder />
        </div>
      )}

      {/* Advanced Options Row */}
      {hasMeasures && (FEATURE_FLAGS.PERIOD_COMPARISON || FEATURE_FLAGS.WINDOW_FUNCTIONS) && (
        <div className="space-y-6">
          <h3 className="text-sm font-semibold text-gray-700">Advanced Options</h3>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Period Comparison Card */}
            {FEATURE_FLAGS.PERIOD_COMPARISON && hasDateColumns && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarBlank className="h-4 w-4 text-teal-600" />
                    <span className="text-sm font-medium text-gray-700">Period Comparison</span>
                  </div>
                  <button
                    onClick={() => setShowPeriodPanel(true)}
                    className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                      periodComparison
                        ? 'border border-teal-300 bg-teal-50 text-teal-700'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {periodComparison ? 'Edit' : 'Configure'}
                  </button>
                </div>
                {periodComparison && (
                  <p className="mt-2 text-xs text-teal-600">
                    Active: {periodComparison.compare_type === 'yoy' ? 'Year over Year' : periodComparison.compare_type === 'mom' ? 'Month over Month' : periodComparison.compare_type === 'qoq' ? 'Quarter over Quarter' : 'Custom Range'}
                  </p>
                )}
              </div>
            )}

            {/* Window Functions Card */}
            {FEATURE_FLAGS.WINDOW_FUNCTIONS && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FnIcon className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-700">Window Functions</span>
                  </div>
                  <button
                    onClick={() => setShowWindowBuilder(true)}
                    className="rounded border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Add
                  </button>
                </div>
                {windowFunctions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {windowFunctions.map(wf => (
                      <div key={wf.key} className="flex items-center justify-between rounded bg-indigo-50 px-2 py-1">
                        <span className="text-xs text-indigo-700">{wf.label} ({WINDOW_FUNC_LABELS[wf.function]})</span>
                        <button onClick={() => removeWindowFunction(wf.key)} className="rounded p-0.5 text-indigo-400 hover:text-red-500">
                          <Trash className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showPeriodPanel && <PeriodComparisonPanel onClose={() => setShowPeriodPanel(false)} />}
      {showWindowBuilder && <WindowFunctionBuilder onClose={() => setShowWindowBuilder(false)} />}
    </div>
  );
}
